/** @jsx isolateComponent */
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { css } from '@emotion/core';

import { Button } from '../../navigation/Button';
import { Tooltip } from '../../display/Tooltip';
import { TextInput } from '../../forms/TextInput';
import type { TextInputProps } from '../../forms/TextInput';
import { useCopyToClipboard } from '../../../hooks';

const Styles = labelStyles('CopyTextInput', {
  root: css`
    display: flex;
  `,
  textInput: css`
    flex: 1;
  `,
});

type CopyTextInputProps = Omit<
  TextInputProps,
  'isMultiline' | 'isFullWidth'
> & {
  /** Same value as TextInputProps and it is required */
  value: string;
};

export const CopyTextInput = (props: CopyTextInputProps) => {
  const [didCopy, handleCopyText] = useCopyToClipboard(props.value);
  const onClickCopy = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    handleCopyText();
  };
  const ariaLabel = didCopy
    ? `"${props.value}" copied to the clipboard`
    : `Copy "${props.value}" to the clipboard`;
  return (
    <div css={Styles.root}>
      <div css={Styles.textInput}>
        <TextInput
          {...props}
          ariaHidden={true}
          borderRadiusSide="left"
          isDisabled={!props.value}
          isReadonly={Boolean(props.value)}
          isFocusable={false}
        />
      </div>
      <Button
        ariaHidden={!props.value}
        ariaLabel={ariaLabel}
        borderRadiusSide="right"
        color="grey"
        disabled={!props.value}
        onClick={onClickCopy}
        size="small"
      >
        Copy
        {didCopy && (
          <Tooltip ariaHidden={true} position="southeast">
            Copied to clipboard!
          </Tooltip>
        )}
      </Button>
    </div>
  );
};

// default props copied from TextInput
CopyTextInput.defaultProps = {
  color: 'grey100',
  size: 'small',
  type: 'text',
};
